import PropTypes from "prop-types";
import { Box, Typography, Chip, Stack, Divider, Paper, List, ListItem, ListItemText } from "@mui/material";

const formatMoney = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      }).format(Number(n));

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
    {children}
  </Typography>
);

SectionTitle.propTypes = { children: PropTypes.node };

const BulletList = ({ items, empty = "None" }) => {
  if (!items?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <List dense disablePadding>
      {items.map((t, i) => (
        <ListItem key={i} disableGutters sx={{ py: 0.25, display: "list-item", listStyle: "disc", ml: 2 }}>
          <ListItemText primary={t} primaryTypographyProps={{ variant: "body2" }} />
        </ListItem>
      ))}
    </List>
  );
};

BulletList.propTypes = { items: PropTypes.arrayOf(PropTypes.string), empty: PropTypes.string };

export function RecommendationSections({ recommendation, dense = false }) {
  if (!recommendation) return null;

  const r = recommendation;
  const spacing = dense ? 1 : 1.5;

  return (
    <Stack spacing={spacing}>
      <Typography variant="caption" color="text.secondary">
        Generated {new Date(r.dateGenerated).toLocaleString()}
      </Typography>

      {r.overallRecommendation && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="primary">
            Overall
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 1 }}>
            <Chip size="small" label={r.overallRecommendation.recommendationLevel || "—"} color="primary" variant="outlined" />
            <Chip size="small" label={`Score ${r.overallRecommendation.score ?? "—"}`} />
            {r.overallRecommendation.timeHorizon && (
              <Chip size="small" label={`Horizon: ${r.overallRecommendation.timeHorizon}`} variant="outlined" />
            )}
          </Box>
          {r.overallRecommendation.thesis && (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {r.overallRecommendation.thesis}
            </Typography>
          )}
          {r.overallRecommendation.keyReasons?.length > 0 && (
            <>
              <SectionTitle>Key reasons</SectionTitle>
              <BulletList items={r.overallRecommendation.keyReasons} />
            </>
          )}
        </Paper>
      )}

      {r.trendAnalysis && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline">Trend</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            <Chip size="small" label={`ST: ${r.trendAnalysis.shortTermTrend ?? "—"}`} />
            <Chip size="small" label={`MT: ${r.trendAnalysis.mediumTermTrend ?? "—"}`} />
            <Chip size="small" label={`LT: ${r.trendAnalysis.longTermTrend ?? "—"}`} />
            <Chip size="small" label={`Overall: ${r.trendAnalysis.trend ?? "—"}`} />
          </Stack>
          {r.trendAnalysis.keyDrivers?.length > 0 && <BulletList items={r.trendAnalysis.keyDrivers} />}
          {r.trendAnalysis.explanation && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
              {r.trendAnalysis.explanation}
            </Typography>
          )}
        </Paper>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
        {r.pricePredictions && (
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="overline">Price outlook</Typography>
            <Typography variant="body2">1W: {formatMoney(r.pricePredictions.oneWeek)}</Typography>
            <Typography variant="body2">1M: {formatMoney(r.pricePredictions.oneMonth)}</Typography>
            <Typography variant="body2">3M: {formatMoney(r.pricePredictions.threeMonths)}</Typography>
            <Typography variant="body2">Bull: {formatMoney(r.pricePredictions.bullCase)} · Bear: {formatMoney(r.pricePredictions.bearCase)}</Typography>
            {r.pricePredictions.explanation && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                {r.pricePredictions.explanation}
              </Typography>
            )}
          </Paper>
        )}
        {r.volatilityAnalysis && (
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="overline">Volatility</Typography>
            <Chip sx={{ my: 1 }} size="small" label={r.volatilityAnalysis.level} />
            <Typography variant="body2">Score: {r.volatilityAnalysis.volatilityScore?.toFixed?.(3) ?? r.volatilityAnalysis.volatilityScore}</Typography>
            {r.volatilityAnalysis.explanation && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                {r.volatilityAnalysis.explanation}
              </Typography>
            )}
          </Paper>
        )}
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
        {r.riskAssessment && (
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="overline">Risk</Typography>
            <Chip sx={{ my: 1 }} size="small" label={r.riskAssessment.level} color="warning" variant="outlined" />
            <Typography variant="body2">Score: {r.riskAssessment.riskScore?.toFixed?.(2) ?? r.riskAssessment.riskScore}</Typography>
            {r.riskAssessment.explanation && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                {r.riskAssessment.explanation}
              </Typography>
            )}
          </Paper>
        )}
        {r.sentimentAnalysis && (
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="overline">Sentiment</Typography>
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ my: 1 }}>
              <Chip size="small" label={r.sentimentAnalysis.level} />
              <Chip size="small" label={`News ${r.sentimentAnalysis.newsScore?.toFixed?.(2) ?? "—"}`} variant="outlined" />
              <Chip size="small" label={`Social ${r.sentimentAnalysis.socialScore?.toFixed?.(2) ?? "—"}`} variant="outlined" />
            </Stack>
            {r.sentimentAnalysis.keySentimentDrivers?.length > 0 && <BulletList items={r.sentimentAnalysis.keySentimentDrivers} />}
            {r.sentimentAnalysis.explanation && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                {r.sentimentAnalysis.explanation}
              </Typography>
            )}
          </Paper>
        )}
      </Stack>

      {r.technicalIndicators && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline">Technical</Typography>
          <Typography variant="body2">
            RSI {r.technicalIndicators.rsi?.toFixed?.(2) ?? "—"} · MACD {r.technicalIndicators.macd?.toFixed?.(4) ?? "—"}
          </Typography>
          {r.technicalIndicators.backtest && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Backtest ROI: {r.technicalIndicators.backtest.simulatedROI?.toFixed?.(2) ?? "—"} — {r.technicalIndicators.backtest.summary}
            </Typography>
          )}
        </Paper>
      )}

      {r.keyLevels && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline">Key levels</Typography>
          <Typography variant="body2">Support: {(r.keyLevels.supportLevels || []).join(", ") || "—"}</Typography>
          <Typography variant="body2">Resistance: {(r.keyLevels.resistanceLevels || []).join(", ") || "—"}</Typography>
          <Typography variant="body2">Pivot: {r.keyLevels.pivotPoint ?? "—"}</Typography>
          {r.keyLevels.explanation && (
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
              {r.keyLevels.explanation}
            </Typography>
          )}
        </Paper>
      )}

      {r.catalystsAndRisks && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline">Catalysts & risks</Typography>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Positive
          </Typography>
          <BulletList items={r.catalystsAndRisks.positiveCatalysts} />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Risks
          </Typography>
          <BulletList items={r.catalystsAndRisks.negativeRisks} />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Upcoming
          </Typography>
          <BulletList items={r.catalystsAndRisks.upcomingEvents} />
          {r.catalystsAndRisks.explanation && (
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
              {r.catalystsAndRisks.explanation}
            </Typography>
          )}
        </Paper>
      )}

      {r.autoBuySellSchedules?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline">Suggested schedules</Typography>
          {r.autoBuySellSchedules.map((s, idx) => (
            <Box key={idx} sx={{ mt: 1 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2">
                Buy {s.buyDay} @ {String(s.buyTime)} → Sell {s.sellDay ?? "—"} @ {s.sellTime ? String(s.sellTime) : "—"}
              </Typography>
              {s.rationale && (
                <Typography variant="body2" color="text.secondary">
                  {s.rationale}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Stack>
  );
}

RecommendationSections.propTypes = {
  recommendation: PropTypes.object,
  dense: PropTypes.bool,
};
